import type { ApiResponse } from '@shared/types'
import axios, { type AxiosError } from 'axios'
import { getErrorText } from 'v2/shared/utils/api-error-text'

// Last-resort copy when a 4xx carries no usable message at all.
const CARD_ADD_FALLBACK_DESCRIPTION =
  'Something went wrong while adding your card. Please try again.'

/**
 * Builds the toast content for a failed Stripe add-card completion, shared by the
 * in-page (StripeEmbeddedCheckout) and redirect-return (PaymentCallback) paths.
 *
 * A terminal client rejection (any 4xx — e.g. an India-issued card on a USD
 * account, fraud, a non-card method, or a cross-account session) is stated as a
 * hard failure with the backend's own reason, because the
 * `checkout.session.completed` webhook re-runs the same validation and rejects
 * identically, so the card will NOT appear later. The backend message is trimmed;
 * if it is blank/absent we fall back to `getErrorText`, and finally to a generic
 * line — the description is never blank or whitespace-only.
 *
 * A 5xx / network / non-HTTP failure is *unconfirmed*: the webhook may still
 * persist the card (and the caller's cards-list refresh would then surface it),
 * so we avoid a definitive "not added" and ask the user to check back — matching
 * the reassuring tone the Cashfree flow uses for the same uncertainty.
 */
export const getCardCheckoutErrorToast = (err: unknown): { title: string; description: string } => {
  // Guard the nullish case explicitly: axios@0.21.4's isAxiosError does
  // `typeof payload === 'object'`, which is true for null and then throws on
  // `null.isAxiosError`. A `null`/`undefined` rejection must fall through to the
  // unconfirmed branch, not crash the caller's catch.
  if (err != null && axios.isAxiosError(err)) {
    const status = err.response?.status
    if (status !== undefined && status >= 400 && status < 500) {
      const rawMessage = (err.response?.data as ApiResponse<unknown> | undefined)?.message
      const backendMessage = typeof rawMessage === 'string' ? rawMessage.trim() : ''
      const description =
        backendMessage ||
        getErrorText(err as AxiosError<ApiResponse<unknown>>).trim() ||
        CARD_ADD_FALLBACK_DESCRIPTION
      return { title: 'Card not added', description }
    }
  }
  return {
    title: "Couldn't confirm your card",
    description: "If it doesn't appear shortly, please try again.",
  }
}
